/**
 * Process-read cache truth for involvement-driven access.
 *
 * Involvement grants process read (ADR 0014): an open assignment or a pending
 * who-else nomination is itself a read grant. Acting on one therefore changes
 * what the acting user may read AND what the process contains, so the two
 * outcomes need different cache treatment — hence the pair below rather than a
 * blanket invalidation.
 */

import type { QueryClient } from '@tanstack/react-query';
import { processKeys, recordingDashboardKeys } from './query-keys';

/**
 * Declining/rejecting ends the involvement that granted the read. Refetching
 * would 403, and an invalidated-but-kept entry is still served from cache on
 * the next mount — which is exactly how a process the user may no longer open
 * keeps rendering (step-preview map dialogs read this key). Cancel any in-flight
 * fetch first so it cannot repopulate what we are about to drop.
 *
 * Prefix matching also drops the process' `flowLayout` entries, which are
 * scoped to the same revoked read.
 *
 * The lists, the org-wide map and the connections are involvement-driven too
 * (ADR 0014), and the app QueryClient's 5-minute `staleTime` means a still-fresh
 * one would keep offering the revoked process as a link straight into a 403
 * page — so mark the whole process family stale. Invalidate rather than remove:
 * those entries hold every other process the user legitimately reads.
 * `refetchType: 'none'` because none of the reject/decline surfaces render a
 * process list; forcing a refetch here would only delay their success screen,
 * and `refetchOnMount` refreshes the stale entry when one is next viewed.
 *
 * Not `invalidateEntity(queryClient, 'process')`: that registry also refreshes
 * recordings, chains and org keys, which a revoked read does not change — the
 * mutations invalidate the task and dashboard keys they own themselves.
 */
export async function dropRevokedProcessRead(
  queryClient: QueryClient,
  processId: string
): Promise<void> {
  const queryKey = processKeys.detail(processId);
  await queryClient.cancelQueries({ queryKey });
  queryClient.removeQueries({ queryKey });
  await queryClient.invalidateQueries({ queryKey: processKeys.all, refetchType: 'none' });
}

/**
 * Accepting/merging keeps the read (the assignment turns into a recording the
 * user is referenced on) but changes the process itself — the placeholder the
 * step-preview map draws becomes a real recording.
 *
 * `refetchType: 'none'` because both call sites close their modal and navigate
 * on success: an active refetch would fetch the redrawn map for a host that is
 * about to unmount. The stale entry refetches when the map is next opened.
 */
export function refreshProcessDetail(queryClient: QueryClient, processId: string): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: processKeys.detail(processId),
    refetchType: 'none'
  });
}

/**
 * The process status is derived at read time from survey rows and the
 * completion checker's verdict (plan 037), so every mutation that can move
 * either — a survey answer, a merge, a publish — must refresh the surfaces
 * that render it: the list, the org-wide map and the Aufnahme-Cockpit. The
 * detail is left to the caller, which decides per mutation whether the read
 * survived.
 */
export function invalidateProcessStatus(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: processKeys.list() });
  queryClient.invalidateQueries({ queryKey: processKeys.map() });
  queryClient.invalidateQueries({ queryKey: recordingDashboardKeys.all });
}
